import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Stack,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { API_BASE_URL } from "../config";
import { useMe } from "../MeContext";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";
import { exportInventoryTypes } from "../exportExcel";
import HintedSelect from "../components/HintedSelect";
import {
  INVENTORY_CATEGORIES as CATEGORIES,
  INVENTORY_CATEGORY_COLOR as CATEGORY_COLOR,
  CONTAINER_CATEGORY,
  STOCK_ROLES,
  STOCK_ROLE_LABEL,
  DRUGS_CATEGORY,
  DRUG_ROUTES,
  DRUG_CLASSES,
  DRUG_LEGAL_CATEGORIES,
  DRUG_AUTHORITIES,
  DRUG_AUTHORITY_GROUPS,
  DRUG_DOSE_UNITS,
  categoryHint,
  drugDisplayName,
  typeLabel,
} from "../constants";

const API = `${API_BASE_URL}/inventory/types`;

const BLANK = {
  name: "",
  category: "",
  notes: "",
  expiry_grace_years: "",
  can_allocate: false,
  stock_role: "",
  // The Drugs-only formulary fields; sent as null for any other category.
  drug_common_name: "",
  drug_presentation: "",
  drug_routes: [],
  drug_dose_quantity: "",
  drug_dose_unit: "",
  drug_class: "",
  drug_legal_category: "",
  drug_schedule_17: false,
  drug_schedule_19: false,
  ...Object.fromEntries(DRUG_AUTHORITY_GROUPS.map(g => [`drug_authority_${g.key}`, "No"])),
  reorder_supplier: "",
  reorder_link: "",
  reorder_price: "",
};

const DRUG_TEXT_FIELDS = ["drug_common_name", "drug_presentation", "drug_class", "drug_legal_category", "drug_dose_unit"];
const DRUG_AUTHORITY_FIELDS = DRUG_AUTHORITY_GROUPS.map(g => `drug_authority_${g.key}`);


// Matches the ceiling on expiry_grace_years in backend/app/schemas/inventory_type.py.
const MAX_GRACE_YEARS = 50;

// How long items of a type may be held past the expiry date printed on them before
// the inventory calls them out — whole years, blank for none. Said the same way in
// the list and in the form. See expiryFlag() in pages/InventoryItems.jsx, which is
// where it's actually applied.
const graceText = (t) => {
  const years = Number(t?.expiry_grace_years) || 0;
  return years ? `${years} year${years === 1 ? "" : "s"}` : "";
};

// Case-insensitive, and "Bag 2" before "Bag 10" rather than after it.
const cmpText = (a, b) => (a || "").localeCompare(b || "", undefined, { numeric: true, sensitivity: "base" });

const SORT_NAME = "Name";
const SORT_OPTIONS = [SORT_NAME, "Category"];

export default function InventoryTypes() {
  const { can } = useMe();
  const navigate = useNavigate();
  const fullScreen = useFullScreenDialog();
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true); // true until the first types fetch resolves
  const [editForm, setEditForm] = useState(BLANK);
  const [editInitial, setEditInitial] = useState(BLANK); // edit-form snapshot, to detect unsaved changes
  const [editing, setEditing] = useState(null); // id being edited, "new" when adding, or null
  const [confirmClose, setConfirmClose] = useState(false); // discard-unsaved-changes prompt
  const [confirmDelete, setConfirmDelete] = useState(false); // delete-confirmation prompt
  // Save posts a new type, so a double-tap would create two (see useWriteLock).
  const { busy: saving, run: runWrite } = useWriteLock();

  // Filters
  const [nameFilter, setNameFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortBy, setSortBy] = useState(SORT_NAME);

  useEffect(() => {
    axios.get(API + "/").then(res => setTypes(res.data)).finally(() => setLoading(false));
  }, []);

  const filtered = types.filter(t =>
    typeLabel(t).toLowerCase().includes(nameFilter.toLowerCase()) &&
    (categoryFilter === "" || t.category === categoryFilter)
  );

  const byName = (a, b) => cmpText(a.name, b.name);
  const COMPARATORS = {
    [SORT_NAME]: byName,
    Category: (a, b) => cmpText(a.category, b.category) || byName(a, b),
  };

  const visible = [...filtered].sort(COMPARATORS[sortBy]);

  // Adding and editing share one dialog, as on the Personnel and Events pages: `editing`
  // is "new" while adding, so the title, Delete button and save call all key off it.
  const openNew = () => {
    setEditForm(BLANK);
    setEditInitial(BLANK);
    setEditing("new");
  };

  const openEdit = (t) => {
    setEditing(t.id);
    const loaded = {
      name: t.name ?? "",
      category: t.category ?? "",
      notes: t.notes ?? "",
      expiry_grace_years: t.expiry_grace_years ?? "",
      can_allocate: Boolean(t.can_allocate),
      stock_role: t.stock_role ?? "",
      ...Object.fromEntries(DRUG_TEXT_FIELDS.map(f => [f, t[f] ?? ""])),
      drug_routes: t.drug_routes ?? [],
      drug_dose_quantity: t.drug_dose_quantity ?? "",
      drug_schedule_17: Boolean(t.drug_schedule_17),
      drug_schedule_19: Boolean(t.drug_schedule_19),
      ...Object.fromEntries(DRUG_AUTHORITY_FIELDS.map(f => [f, t[f] ?? "No"])),
      reorder_supplier: t.reorder_supplier ?? "",
      reorder_link: t.reorder_link ?? "",
      reorder_price: t.reorder_price ?? "",
    };
    setEditForm(loaded);
    setEditInitial(loaded);
  };

  const closeEdit = () => { setEditing(null); setConfirmClose(false); setConfirmDelete(false); };

  // Closing (backdrop, Escape or Cancel) warns first if there are unsaved changes.
  const isDirty = () => JSON.stringify(editForm) !== JSON.stringify(editInitial);
  const requestClose = () => (isDirty() ? setConfirmClose(true) : closeEdit());

  const saveEdit = () => runWrite(() => {
    const isDrug = editForm.category === DRUGS_CATEGORY;
    const payload = {
      ...editForm,
      // Blank is no grace at all, which is null rather than 0 — the column records
      // whether one was ever set, and the two behave the same either way.
      expiry_grace_years: String(editForm.expiry_grace_years).trim() === ""
        ? null : Math.trunc(Number(editForm.expiry_grace_years)),
      reorder_price: editForm.reorder_price === "" ? null : Number(editForm.reorder_price),
      // Only a container type plays a part in stock; switching the category away
      // from Storage drops it rather than leaving a role the backend would refuse.
      stock_role: editForm.category === CONTAINER_CATEGORY && editForm.stock_role
        ? editForm.stock_role : null,
      // The formulary fields belong to a Drugs type only (the backend clears them on
      // any other category too); a blank choice is null, not "".
      ...Object.fromEntries(DRUG_TEXT_FIELDS.map(f =>
        [f, isDrug && String(editForm[f]).trim() ? String(editForm[f]).trim() : null])),
      // Kept in the list's own order, so "IM or IV" reads the same however it was ticked.
      drug_routes: isDrug && editForm.drug_routes.length
        ? DRUG_ROUTES.map(r => r.value).filter(v => editForm.drug_routes.includes(v)) : null,
      drug_dose_quantity: isDrug && String(editForm.drug_dose_quantity).trim() !== ""
        ? Number(editForm.drug_dose_quantity) : null,
      drug_schedule_17: isDrug && editForm.drug_schedule_17,
      drug_schedule_19: isDrug && editForm.drug_schedule_19,
      ...Object.fromEntries(DRUG_AUTHORITY_FIELDS.map(f => [f, isDrug ? editForm[f] : null])),
    };
    if (editing === "new") {
      return axios.post(API + "/", payload).then(res => {
        setTypes([...types, res.data]);
        closeEdit();
      });
    }
    return axios.put(`${API}/${editing}`, payload).then(res => {
      setTypes(types.map(t => (t.id === editing ? res.data : t)));
      closeEdit();
    });
  });

  const removeType = () => {
    axios.delete(`${API}/${editing}`).then(() => {
      setTypes(types.filter(t => t.id !== editing));
      closeEdit();
    });
  };

  // Whole years, none of them negative, and capped where schemas/inventory_type.py
  // caps it — so a mistyped grace is refused here rather than coming back a 422.
  const graceEntry = String(editForm.expiry_grace_years).trim();
  const graceOk = graceEntry === "" ||
    (Number.isInteger(Number(graceEntry)) && Number(graceEntry) >= 0 && Number(graceEntry) <= MAX_GRACE_YEARS);

  // A single dose is a positive amount, or blank for "varies — say it when giving".
  const doseEntry = String(editForm.drug_dose_quantity).trim();
  const doseOk = doseEntry === "" || Number(doseEntry) > 0;
  // "500" means nothing without "mg".
  const doseUnitMissing = doseEntry !== "" && !editForm.drug_dose_unit;

  const canSave = editForm.name.trim() && editForm.category && graceOk
    && (editForm.category !== DRUGS_CATEGORY || (doseOk && !doseUnitMissing));

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h4">Inventory Types</Typography>
        {/* Types has no nav tab of its own — it's reached from the Inventory page. */}
        <Button startIcon={<ArrowBackIcon />} sx={{ ml: "auto" }}
                onClick={() => navigate("/inventory-items")}>
          Back to inventory
        </Button>
        {can("InventoryTypes.export") && (
          <Button variant="outlined" startIcon={<FileDownloadIcon />}
                  disabled={visible.length === 0}
                  onClick={() => exportInventoryTypes(visible)}
                  sx={{ display: { xs: "none", sm: "inline-flex" } }}>
            Export to Excel
          </Button>
        )}
        {can("InventoryTypes.add") && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Add type</Button>
        )}
      </Stack>

      {/* Filters */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
        <TextField label="Filter by name" size="small" value={nameFilter}
                   onChange={e => setNameFilter(e.target.value)} />
        <TextField label="Category" select size="small" sx={{ minWidth: 160 }} value={categoryFilter}
                   onChange={e => setCategoryFilter(e.target.value)}>
          <MenuItem value=""><em>All categories</em></MenuItem>
          {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField label="Sort by" select size="small" sx={{ minWidth: 150 }} value={sortBy}
                   onChange={e => setSortBy(e.target.value)}>
          {SORT_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <Typography variant="body2" color="text.secondary" sx={{ ml: { sm: "auto" } }}>
          {visible.length} of {types.length}
        </Typography>
      </Stack>

      {/* List */}
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                <TableCell>Name</TableCell>
                <TableCell>Category</TableCell>
                {/* A grace set here silences the expiry flags on every item of the
                    type, so it belongs where it can be seen without opening each one. */}
                <TableCell>Expiry grace</TableCell>
                {/* Which types are offered when kit is allocated to someone for an
                    event — worth seeing without opening each one, since a type that
                    isn't ticked simply never appears in that picker. */}
                <TableCell>Allocatable</TableCell>
                <TableCell>Supplier</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map(t => (
                <TableRow hover key={t.id} onClick={() => openEdit(t)}>
                  <TableCell>{typeLabel(t)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={t.category} color={CATEGORY_COLOR[t.category] || "default"} />
                    {t.stock_role && (
                      <Chip size="small" variant="outlined" sx={{ ml: 1 }}
                            color={t.stock_role === "quarantine" ? "error" : "primary"}
                            label={STOCK_ROLE_LABEL[t.stock_role]} />
                    )}
                  </TableCell>
                  <TableCell>{graceText(t) || "—"}</TableCell>
                  <TableCell>
                    {t.can_allocate
                      ? <Chip size="small" label="Allocatable" color="info" />
                      : "—"}
                  </TableCell>
                  <TableCell>{t.reorder_supplier || "—"}</TableCell>
                </TableRow>
              ))}
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ color: "text.secondary", py: 4 }}>
                    {loading ? <CircularProgress size={28} />
          : types.length === 0 ? "No types yet — add one." : "No types match your filters."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add / edit dialog */}
      <Dialog open={editing !== null} onClose={requestClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
        <DialogTitle>
          {editing === "new" ? "Add type" : `Edit ${editForm.name || "type"}`}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" fullWidth required autoFocus value={editForm.name}
                       onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
            <TextField label="Category" select fullWidth required value={editForm.category}
                       helperText={categoryHint(editForm.category)}
                       onChange={e => setEditForm({ ...editForm, category: e.target.value })}>
              {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            {/* Whether containers of this type are where drugs and medical stock is
                kept or set aside — "Stock cupboard", "Pending disposal". Set once on
                the type, so it never clutters the items themselves. */}
            {editForm.category === CONTAINER_CATEGORY && (
              <TextField label="Stock role" select fullWidth value={editForm.stock_role}
                         helperText={STOCK_ROLES.find(r => r.value === editForm.stock_role)?.hint
                           || "Only for a stock cupboard, or where stock waits to be disposed of"}
                         onChange={e => setEditForm({ ...editForm, stock_role: e.target.value })}>
                <MenuItem value=""><em>None</em></MenuItem>
                {STOCK_ROLES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </TextField>
            )}
            {/* The formulary facts about a drug — what it is, how it's given, its legal
                status and who may give it on what authority. Drugs only. */}
            {editForm.category === DRUGS_CATEGORY && (
              <>
                {/* Name, common name and presentation make up what the drug is called
                    everywhere else — shown as it will read, so it can be checked. */}
                <TextField label="Common name" fullWidth value={editForm.drug_common_name}
                           helperText="A brand or everyday name, e.g. Calpol"
                           onChange={e => setEditForm({ ...editForm, drug_common_name: e.target.value })} />
                <TextField label="Presentation" fullWidth value={editForm.drug_presentation}
                           helperText={`For example 500 mg tablets, or 500 mg/5 ml solution. `
                             + `Shown as: ${drugDisplayName(editForm) || "—"}`}
                           onChange={e => setEditForm({ ...editForm, drug_presentation: e.target.value })} />
                {/* Usually one route, which giving the drug then takes as read. Tick
                    more than one (IM and IV) for a product that can go in either way,
                    and whoever gives it chooses. */}
                <TextField label="Route" select fullWidth value={editForm.drug_routes}
                           helperText={editForm.drug_routes.length > 1
                             ? `Whoever gives it chooses: ${editForm.drug_routes.join(" or ")}`
                             : "Tick more than one if it can be given either way (e.g. IM or IV)"}
                           slotProps={{ select: {
                             multiple: true,
                             renderValue: (v) => DRUG_ROUTES.map(r => r.value)
                               .filter(r => v.includes(r)).join(" or "),
                           } }}
                           onChange={e => setEditForm({ ...editForm, drug_routes: e.target.value })}>
                  {DRUG_ROUTES.map(r => (
                    <MenuItem key={r.value} value={r.value}>
                      <Checkbox size="small" checked={editForm.drug_routes.includes(r.value)}
                                sx={{ p: 0, mr: 1 }} />
                      {r.value}
                      {r.hint && (
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                          {r.hint}
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </TextField>
                {/* One dose as packed — 500 mg a tablet — so giving it is "2 ×". The
                    unit is what every dose of this drug is recorded in. */}
                <Stack direction="row" spacing={2}>
                  <TextField label="Single dose quantity" type="number" sx={{ flex: 1 }}
                             value={editForm.drug_dose_quantity} error={!doseOk}
                             slotProps={{ htmlInput: { min: 0, step: "any" } }}
                             helperText={!doseOk ? "A positive amount, or blank"
                               : "Blank if it varies — the amount is then entered each time"}
                             onChange={e => setEditForm({ ...editForm, drug_dose_quantity: e.target.value })} />
                  <TextField label="Dose unit" select sx={{ flex: 1 }} value={editForm.drug_dose_unit}
                             error={doseUnitMissing}
                             helperText={doseUnitMissing ? "Needed with a single dose quantity"
                               : "Every dose of it is recorded in this"}
                             onChange={e => setEditForm({ ...editForm, drug_dose_unit: e.target.value })}>
                    <MenuItem value=""><em>Not set</em></MenuItem>
                    {DRUG_DOSE_UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                  </TextField>
                </Stack>
                <HintedSelect label="Class" options={DRUG_CLASSES.map(c => ({ value: c }))}
                              value={editForm.drug_class}
                              onChange={v => setEditForm({ ...editForm, drug_class: v })} />
                <HintedSelect label="Legal category" options={DRUG_LEGAL_CATEGORIES}
                              value={editForm.drug_legal_category}
                              onChange={v => setEditForm({ ...editForm, drug_legal_category: v })} />
                <FormControlLabel
                  control={<Checkbox checked={editForm.drug_schedule_17}
                                     onChange={e => setEditForm({ ...editForm, drug_schedule_17: e.target.checked })} />}
                  label={
                    <Box>
                      <Typography variant="body1">Schedule 17</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Exemption for registered paramedics to give without a prescription, PSD or PGD
                      </Typography>
                    </Box>
                  } />
                <FormControlLabel
                  control={<Checkbox checked={editForm.drug_schedule_19}
                                     onChange={e => setEditForm({ ...editForm, drug_schedule_19: e.target.checked })} />}
                  label={
                    <Box>
                      <Typography variant="body1">Schedule 19</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Exemption for all staff to give to save life in an emergency (but must
                        escalate immediately to the Medical Director or Clinical Lead)
                      </Typography>
                    </Box>
                  } />
                <Typography variant="subtitle2" sx={{ pt: 1 }}>Who can give it, and on what authority</Typography>
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                  {DRUG_AUTHORITY_GROUPS.map(g => {
                    const f = `drug_authority_${g.key}`;
                    return (
                      <HintedSelect key={g.key} label={g.label} options={DRUG_AUTHORITIES}
                                    allowBlank={false} value={editForm[f]} size="small"
                                    onChange={v => setEditForm({ ...editForm, [f]: v })} />
                    );
                  })}
                </Box>
              </>
            )}
            <TextField label="Notes" fullWidth multiline value={editForm.notes}
                       onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
            {/* Whole years, blank for none — which is how every type behaves until one
                is set here. It only moves the point at which the inventory flags a
                date; nothing stored on an item changes. */}
            <TextField label="Expiry grace period (years)" type="number" fullWidth
                       value={editForm.expiry_grace_years}
                       error={!graceOk}
                       slotProps={{ htmlInput: { min: 0, max: MAX_GRACE_YEARS, step: 1 } }}
                       helperText={!graceOk
                         ? `Whole years, 0 to ${MAX_GRACE_YEARS} — or leave it blank for none`
                         : "How long items of this type can be kept past the expiry date printed "
                           + "on them before they're flagged: red once the grace has run out, "
                           + "amber for the 30 days before. Blank means the printed date is the deadline."}
                       onChange={e => setEditForm({ ...editForm, expiry_grace_years: e.target.value })} />
            {/* Whether items of this type can be handed out to someone for an event.
                Nothing about the item changes — it only decides what the allocation
                picker offers. Un-ticking it stops new allocations and leaves the ones
                already recorded alone, so kit still out can be marked Returned. */}
            <FormControlLabel
              control={<Checkbox checked={editForm.can_allocate}
                                 onChange={e => setEditForm({ ...editForm, can_allocate: e.target.checked })} />}
              label="Can be allocated to a person for an event" />
            <TextField label="Reorder supplier" fullWidth value={editForm.reorder_supplier}
                       onChange={e => setEditForm({ ...editForm, reorder_supplier: e.target.value })} />
            <TextField label="Reorder link" fullWidth value={editForm.reorder_link}
                       onChange={e => setEditForm({ ...editForm, reorder_link: e.target.value })} />
            <TextField label="Reorder price" type="number" fullWidth value={editForm.reorder_price}
                       onChange={e => setEditForm({ ...editForm, reorder_price: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {editing !== "new" && can("InventoryTypes.delete") && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
              Delete
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={requestClose}>Cancel</Button>
          {(editing === "new" ? can("InventoryTypes.add") : can("InventoryTypes.edit")) && (
            <Button variant="contained" disabled={!canSave || saving} onClick={saveEdit}
                    startIcon={saving ? <CircularProgress size={16} /> : undefined}>
              {saving ? "Saving…" : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete {editForm.name || "type"}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the "{editForm.name}" type. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={removeType}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved-changes confirmation */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes to this type. If you close now, they will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={closeEdit}>Discard changes</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
